'use client';

import { useEffect, useState } from 'react';
import type { ContentModeration, ManualObfuscationJobStatus } from '@/shared/cors-provenance-api';
import {
  clearManualObfuscationJob,
  isLocalManualObfuscationInProgress,
  isLocalObfuscationBaselineReviewed,
  subscribeManualObfuscationJobs,
} from '@/lib/manual-obfuscation-job';

function statusFrom(
  value?: ManualObfuscationJobStatus | ContentModeration | null,
): ManualObfuscationJobStatus | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.manualObfuscationJobStatus ?? null;
}

/**
 * Effective blur-job status: provenance ``contentModeration`` wins, with a
 * local IN_PROGRESS overlay until file-info catches up after a user flag.
 * A terminal status from provenance retires that overlay.
 */
export function useManualObfuscationJobStatus(
  fileUrl?: string | null,
  contentModerationOrStatus?: ManualObfuscationJobStatus | ContentModeration | null,
): ManualObfuscationJobStatus | null {
  const [, setTick] = useState(0);

  useEffect(() => subscribeManualObfuscationJobs(() => setTick((n) => n + 1)), []);

  const fromApi = statusFrom(contentModerationOrStatus);
  const findingStatus =
    contentModerationOrStatus && typeof contentModerationOrStatus === 'object'
      ? contentModerationOrStatus.findingStatus
      : null;
  const localPending = isLocalManualObfuscationInProgress(fileUrl);
  const baselineReviewed = isLocalObfuscationBaselineReviewed(fileUrl);
  // Re-flag: the local job started against an already-REVIEWED row. A verifier
  // clear during a blur did not, and must drop the overlay.
  const staleCleared = findingStatus === 'REVIEWED' && localPending && baselineReviewed;
  const clearedDuringBlur = findingStatus === 'REVIEWED' && !baselineReviewed;
  useEffect(() => {
    if (!fileUrl) return;
    if (clearedDuringBlur && localPending) {
      clearManualObfuscationJob(fileUrl);
      return;
    }
    if (!staleCleared && (fromApi === 'COMPLETED' || fromApi === 'FAILED')) {
      clearManualObfuscationJob(fileUrl);
    }
  }, [clearedDuringBlur, localPending, staleCleared, fromApi, fileUrl]);

  if (clearedDuringBlur) return null;
  if (!staleCleared && (fromApi === 'COMPLETED' || fromApi === 'FAILED')) {
    return fromApi;
  }
  if (fromApi === 'IN_PROGRESS' || localPending) {
    return 'IN_PROGRESS';
  }
  return fromApi;
}
